'use client';

import React from 'react';
import { X, User, Clock, Tag, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import type { AuditLog } from '@/lib/services/staffingService';

interface AuditDetailModalProps {
  log: AuditLog;
  onClose: () => void;
}

const SEVERITY_CONFIG: Record<string, { icon: React.ElementType; className: string; label: string }> = {
  info: { icon: Info, className: 'text-primary bg-primary/10', label: 'Info' },
  warning: { icon: AlertTriangle, className: 'text-warning bg-warning/10', label: 'Warning' },
  critical: { icon: AlertCircle, className: 'text-danger bg-danger/10', label: 'Critical' },
};

const ACTION_LABELS: Record<string, string> = {
  role_change: 'Role Change',
  credential_update: 'Credential Update',
  shift_modification: 'Shift Modification',
  approval: 'Approval',
  login: 'Login',
  logout: 'Logout',
  user_created: 'User Created',
  user_deactivated: 'User Deactivated',
  invoice_created: 'Invoice Created',
  invoice_updated: 'Invoice Updated',
  timesheet_approved: 'Timesheet Approved',
  timesheet_rejected: 'Timesheet Rejected',
  job_posted: 'Job Posted',
  applicant_stage_changed: 'Applicant Stage Changed',
  settings_updated: 'Settings Updated',
};

function JsonBlock({ data }: { data: Record<string, unknown> }) {
  return (
    <pre className="text-xs bg-muted/60 rounded-lg p-3 overflow-auto max-h-32 text-foreground/80 font-mono">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function AuditDetailModal({ log, onClose }: AuditDetailModalProps) {
  const sev = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.info;
  const SevIcon = sev.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${sev.className}`}>
              <SevIcon size={18} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                {ACTION_LABELS[log.actionType] || log.actionType}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">{log.targetEntity}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Description */}
          <p className="text-sm text-foreground leading-relaxed">{log.description}</p>

          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-muted/40 rounded-xl p-3 flex items-start gap-2.5">
              <User size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-600 mb-0.5">Performed By</p>
                <p className="text-sm font-medium text-foreground truncate">{log.performedByName}</p>
                <p className="text-xs text-muted-foreground">{log.performedByRole}</p>
              </div>
            </div>
            <div className="bg-muted/40 rounded-xl p-3 flex items-start gap-2.5">
              <Clock size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-600 mb-0.5">Timestamp</p>
                <p className="text-xs text-foreground">{formatDateTime(log.createdAt)}</p>
              </div>
            </div>
            <div className="bg-muted/40 rounded-xl p-3 flex items-start gap-2.5">
              <Tag size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-600 mb-0.5">Target</p>
                <p className="text-sm font-medium text-foreground">{log.targetEntity}</p>
                {log.targetId && <p className="text-xs text-muted-foreground font-mono">{log.targetId}</p>}
              </div>
            </div>
            <div className="bg-muted/40 rounded-xl p-3 flex items-start gap-2.5">
              <SevIcon size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-600 mb-0.5">Severity</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-600 capitalize ${sev.className}`}>
                  {log.severity}
                </span>
              </div>
            </div>
          </div>

          {/* Before / After state */}
          {(log.beforeState || log.afterState) && (
            <div className="space-y-3">
              <p className="text-xs font-600 uppercase tracking-wider text-muted-foreground">State Changes</p>
              <div className="grid grid-cols-2 gap-3">
                {log.beforeState && (
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1.5 font-medium">Before</p>
                    <JsonBlock data={log.beforeState} />
                  </div>
                )}
                {log.afterState && (
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1.5 font-medium">After</p>
                    <JsonBlock data={log.afterState} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* IP / User Agent */}
          {(log.ipAddress || log.userAgent) && (
            <div className="space-y-2">
              <p className="text-xs font-600 uppercase tracking-wider text-muted-foreground">Session Info</p>
              {log.ipAddress && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">IP Address</span>
                  <span className="font-mono text-foreground">{log.ipAddress}</span>
                </div>
              )}
              {log.userAgent && (
                <div className="flex items-start justify-between text-xs gap-4">
                  <span className="text-muted-foreground flex-shrink-0">User Agent</span>
                  <span className="text-foreground text-right break-all">{log.userAgent}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <button
            onClick={onClose}
            className="w-full py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
